import { useAuthState } from '@/app/providers/auth'

import { useEffect, useMemo, useState } from 'react'

import { useExistingOrderStore } from '../../store/existingOrderStore'
import type { ExistingOrderState } from './types'
import { useBasketDesignedUiCompatibleGmOrderData } from './useBasketDesignedUiCompatibleGmOrderDat'
import { useExistingOrderItems } from './useExistingOrderItems'
import { useExistingOrderMutations } from './useExistingOrderMutations'
import { useExistingOrderQueries } from './useExistingOrderQueries'
import { useExistingOrderValidation } from './useExistingOrderValidation'
import { useInputRelatedVariableOrderData } from './useInputRelatedVariableOrderData'
import { useOrderPaymentType } from './useOrderPaymentType'

type Props = {
  orderId?: string
  vendorId?: string
  onSuccessfulSave?: (orderData: { orderId: string }) => void
}

const useExistingOrderInternal = ({ orderId, vendorId, onSuccessfulSave }: Props) => {
  const [loading, setLoading] = useState(false)

  const {
    orderData,
    loading: loadingOrderData,
    called: orderDataCalled,
    loadFailed: orderLoadFailed,
  } = useExistingOrderQueries({ orderId, vendorId })

  const { orderData: basketDesignedUiCompatibleOrderData } =
    useBasketDesignedUiCompatibleGmOrderData({
      orderData,
    })

  const authState = useAuthState()
  const me = authState.type === 'authenticated' ? authState.user : null
  const editorId = me?.id
  const editorName = [me?.firstName, me?.lastName].filter(Boolean).join(' ')
  // Keyed on primitives: authState changes identity on any `me` cache write, and a new editor
  // object would cascade into a full cart re-render through the existing-order store.
  const editor = useMemo(
    () => (editorId ? { id: editorId, name: editorName } : null),
    [editorId, editorName],
  )

  const {
    orderItems,
    orderItemsInternal,
    removedOrderItems,
    changesMade,
    itemQuantity,
    newItemWasJustAdded,
    adjustItemQuantity,
    addNewItemToOrder,
    removeItemFromOrder,
  } = useExistingOrderItems({ orderData, editor })

  const {
    taxRateTotals,
    fees,
    grandTotals,
    subtotalExTaxAsNumber,
    totalIncTaxAsNumber,
    recalculatingFees,
    recalculatingTotals,
    minimumOrderValue,
    userEditCutOffTime,
  } = useInputRelatedVariableOrderData({
    orderData,
    orderItems: changesMade ? orderItemsInternal : null,
  })

  const { paymentMethod } = useOrderPaymentType({ orderData })

  const { canSaveChanges: validationCanSaveChanges, errors: validationErrors } =
    useExistingOrderValidation({
      changesMade,
      minimumOrderValue,
      subtotalExTax: subtotalExTaxAsNumber,
      userEditCutOffTime,
      paymentMethod,
    })

  const {
    saveOrderChanges: runSaveOrderChanges,
    saving,
    saveError,
  } = useExistingOrderMutations({
    orderData,
    removedOrderItems,
    updatedOrderItems: orderItemsInternal,
    updatedOrderTotalIncTax: totalIncTaxAsNumber,
    onSuccessfulSave,
  })

  useEffect(() => {
    if (loadingOrderData) setLoading(true)
  }, [loadingOrderData])

  useEffect(() => {
    if (!orderLoadFailed && orderData?.orderItems.items.length !== orderItems.length) return
    setLoading(false)
  }, [loading, orderItems, orderData?.orderItems.items, orderLoadFailed])

  const value = useMemo<ExistingOrderState>(() => {
    const saveOrderChanges = async () => {
      if (!validationCanSaveChanges || recalculatingFees || recalculatingTotals) return
      await runSaveOrderChanges()
    }

    return {
      orderData,
      basketDesignedUiCompatibleOrderData,
      loadingOrderData: loadingOrderData || loading,
      orderItems,
      orderItemsInternal,
      removedOrderItems,
      changesMade,
      itemQuantity,
      newItemWasJustAdded,
      adjustItemQuantity,
      addNewItemToOrder,
      removeItemFromOrder,
      fees,
      taxRateTotals,
      grandTotals,
      subtotalExTaxAsNumber,
      totalIncTaxAsNumber,
      recalculatingFees,
      recalculatingTotals,
      minimumOrderValue,
      userEditCutOffTime,
      canSaveChanges: !recalculatingTotals && !recalculatingFees && validationCanSaveChanges,
      validationErrors,
      saving,
      orderDataCalled,
      orderLoadFailed,
      saveError,
      saveOrderChanges,
    }
  }, [
    orderData,
    basketDesignedUiCompatibleOrderData,
    loadingOrderData,
    loading,
    orderItems,
    orderItemsInternal,
    removedOrderItems,
    changesMade,
    itemQuantity,
    newItemWasJustAdded,
    adjustItemQuantity,
    addNewItemToOrder,
    removeItemFromOrder,
    fees,
    taxRateTotals,
    grandTotals,
    subtotalExTaxAsNumber,
    totalIncTaxAsNumber,
    recalculatingFees,
    recalculatingTotals,
    minimumOrderValue,
    userEditCutOffTime,
    validationErrors,
    saving,
    orderDataCalled,
    orderLoadFailed,
    saveError,
    validationCanSaveChanges,
    runSaveOrderChanges,
  ])

  return value
}

export const useExistingOrderController = ({ orderId, vendorId, onSuccessfulSave }: Props) => {
  const value = useExistingOrderInternal({ orderId, vendorId, onSuccessfulSave })
  const setExistingOrder = useExistingOrderStore((state) => state.setExistingOrder)
  const resetExistingOrder = useExistingOrderStore((state) => state.resetExistingOrder)

  useEffect(() => {
    setExistingOrder(value)
    return () => {
      resetExistingOrder()
    }
  }, [resetExistingOrder, setExistingOrder, value])

  return value
}

export const useExistingOrder = () => {
  return useExistingOrderStore((store) => store.existingOrder)
}
